// The office copy of the backups (M7-8, CLAUDE.md §6.4 copy 3, docs/plans/PHASE_1_M7-8.md): the
// shared contract between the server (`/api/backups/office/*`) and Dob Desktop in Backup Agent mode.
//
// The agent mirrors the restic repository folder. It never has the key: it sees file names, sizes
// and checksums only. In a restic repository every file except `config` is named by the SHA-256 of
// its content and never changes once written — which lets the agent check each file it receives
// (owner addition O30-1: a file whose content does not match its name is never accepted).
import { z } from 'zod';

/** What a repository path is, or null for anything the agent must never copy or ask for. */
export type RepoFileKind = 'config' | 'key' | 'snapshot' | 'index' | 'data';

const HEX64 = '[0-9a-f]{64}';
const PATTERNS: readonly [RepoFileKind, RegExp][] = [
  ['config', /^config$/],
  ['key', new RegExp(`^keys/${HEX64}$`)],
  ['snapshot', new RegExp(`^snapshots/${HEX64}$`)],
  ['index', new RegExp(`^index/${HEX64}$`)],
  ['data', /^data\/([0-9a-f]{2})\/(\1[0-9a-f]{62})$/],
];

/**
 * The kind of a repository path, with strict names only: `config`, `keys/<sha256>`,
 * `snapshots/<sha256>`, `index/<sha256>`, `data/<first two>/<sha256>`. restic's lock files, its
 * temporary files and anything else (a `..`, an absolute path, a backslash, a second dot) → null.
 */
export function repoFileKind(path: string): RepoFileKind | null {
  for (const [kind, pattern] of PATTERNS) if (pattern.test(path)) return kind;
  return null;
}

/** The SHA-256 a content-addressed file must have (its name); null for `config`. */
export function expectedSha256(path: string): string | null {
  const kind = repoFileKind(path);
  if (kind === null || kind === 'config') return null;
  return path.slice(path.lastIndexOf('/') + 1);
}

const Sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const RepoPath = z
  .string()
  .max(200)
  .refine((p) => repoFileKind(p) !== null, 'not a repository file');
const IsoTime = z.iso.datetime({ offset: true });

export const OfficeManifestFileSchema = z.object({
  path: RepoPath,
  size: z.number().int().nonnegative(),
  /** The name for content-addressed files; computed by the server for `config`. */
  sha256: Sha256,
  /** When the server wrote it (a snapshot file's time ≈ the snapshot's time). */
  mtime: IsoTime,
});
export type OfficeManifestFile = z.infer<typeof OfficeManifestFileSchema>;

/** Settings the agent follows, from `backup.office_copy_*` (changeable without code, plan §7). */
export const OfficeCopySettingsSchema = z.object({
  hour: z.number().int().min(0).max(23),
  lateHours: z.number().int().min(1),
  keepDeletedDays: z.number().int().min(1),
});
export type OfficeCopySettings = z.infer<typeof OfficeCopySettingsSchema>;

export const OfficeManifestSchema = z.object({
  generatedAt: IsoTime,
  environment: z.string().max(40),
  /** The server's last nightly backup (`backup-status.json`), null when none is recorded. */
  serverBackup: z.object({ at: IsoTime, ok: z.boolean() }).nullable(),
  settings: OfficeCopySettingsSchema,
  files: z.array(OfficeManifestFileSchema).max(500_000),
  /** Incidents a partner released (MASS_DELETION, CONFIG_REPLACED): the agent may then proceed. */
  releases: z.array(z.object({ incidentId: z.uuid(), releasedAt: IsoTime })).max(1000),
});
export type OfficeManifest = z.infer<typeof OfficeManifestSchema>;

/**
 * Something the agent refused to do, reported until it is over (alerts to partners and IT, D110
 * rules: once, then daily while it lasts):
 * - `MASS_DELETION` — the server would remove too much at once; nothing of it is deleted locally
 *   until a partner releases it (then the normal keep-for-days rule applies).
 * - `CONFIG_REPLACED` — the server's `config` is not the one the office copy holds (O30-1). The old
 *   one is kept. Released by a partner (a real repository upgrade): the old file is kept aside.
 * - `FILE_REPLACED` — the server lists a file the copy already holds with other content or size
 *   (O30-1). The copy keeps its own verified file. Not released: it ends when the server agrees again.
 * - `HASH_MISMATCH` — a file received whose SHA-256 is not its name (or not the one listed for
 *   `config`). Thrown away, tried again at the next run (O30-1).
 * - `KEY_REMOVED` — a `keys/` file disappeared from the server; the copy keeps it forever.
 */
export const OFFICE_INCIDENT_KINDS = [
  'MASS_DELETION',
  'CONFIG_REPLACED',
  'FILE_REPLACED',
  'HASH_MISMATCH',
  'KEY_REMOVED',
] as const;
export type OfficeIncidentKind = (typeof OFFICE_INCIDENT_KINDS)[number];
export const RELEASABLE_INCIDENTS: readonly OfficeIncidentKind[] = [
  'MASS_DELETION',
  'CONFIG_REPLACED',
];

export const OfficeIncidentSchema = z.object({
  /** Made by the agent when it first sees the incident; the same id while it lasts. */
  id: z.uuid(),
  kind: z.enum(OFFICE_INCIDENT_KINDS),
  detectedAt: IsoTime,
  open: z.boolean(),
  /** The file concerned (FILE_REPLACED, HASH_MISMATCH, KEY_REMOVED, CONFIG_REPLACED). */
  path: RepoPath.nullable(),
  files: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
});
export type OfficeIncident = z.infer<typeof OfficeIncidentSchema>;

export const OFFICE_COPY_STEPS = [
  'manifest',
  'disk',
  'download',
  'verify',
  'write',
  'cleanup',
] as const;
export type OfficeCopyStep = (typeof OFFICE_COPY_STEPS)[number];

export const OfficeCopyReportSchema = z
  .object({
    /** One per run, made by the agent (UUIDv7): a report sent twice is stored once. */
    id: z.uuid(),
    trigger: z.enum(['SCHEDULE', 'STARTUP', 'MANUAL']),
    startedAt: IsoTime,
    finishedAt: IsoTime,
    ok: z.boolean(),
    failedStep: z.enum(OFFICE_COPY_STEPS).nullable(),
    /** Plain words for people (no secrets, no paths outside the copy). */
    error: z.string().max(300).nullable(),
    filesCopied: z.number().int().nonnegative(),
    bytesCopied: z.number().int().nonnegative(),
    /** The office copy after the run. */
    repositoryFiles: z.number().int().nonnegative(),
    repositoryBytes: z.number().int().nonnegative(),
    snapshotCount: z.number().int().nonnegative(),
    /** Server time of the newest snapshot the copy holds (what "late" is measured on). */
    newestSnapshotAt: IsoTime.nullable(),
    /** The server's last backup as the manifest said (null if the manifest was not read). */
    serverBackupAt: IsoTime.nullable(),
    keptDeletedFiles: z.number().int().nonnegative(),
    keptDeletedBytes: z.number().int().nonnegative(),
    freeBytes: z.number().int().nonnegative(),
    incidents: z.array(OfficeIncidentSchema).max(100),
    appVersion: z.string().max(40),
  })
  .refine((r) => r.ok === (r.failedStep === null), {
    message: 'failedStep is set exactly when the run failed',
    path: ['failedStep'],
  });
export type OfficeCopyReport = z.infer<typeof OfficeCopyReportSchema>;
